import { useCallback } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import type { RouterLinkAction } from '@redux-first-router-link';
import { RouterLink } from '@redux-first-router-link';
import { SWS_HOST } from '@/constants';

export interface Props {
  to: string | RouterLinkAction;
  children: ReactNode;
  onClick?: (e: MouseEvent) => void;
  className?: string;
  openedNewTab?: boolean;
}

const UniversalLink = ({
  to,
  children,
  onClick,
  className,
  openedNewTab = false,
}: Props) => {
  const handleNativeExternalLinkClick = useCallback(
    async (e: MouseEvent, url: string) => {
      if (onClick) {
        onClick(e);
      }
      try {
        e.preventDefault();
        e.stopPropagation();
        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ url });
      } catch (error) {
        console.log('Capacitor Browser error', error);
      }
    },
    [onClick]
  );

  const isSWSLink =
    typeof to === 'string' &&
    (to.includes('simplywall.st') || to.startsWith('/'));
  const isInternalLink =
    typeof to === 'object' ||
    (isSWSLink &&
      !(
        to.includes('/news') ||
        to.includes('feedback.simplywall.st') ||
        to.includes('learn.simplywall.st') ||
        to.includes('support.simplywall.st')
      ));

  // if url is object or internal link, use RouterLink
  if (isInternalLink) {
    const matchFullUrl =
      typeof to === 'string' && to.match(/^https:\/\/.*simplywall.st(.+)/);
    const path = matchFullUrl ? matchFullUrl[1] : to;
    return (
      <RouterLink
        to={path}
        onClick={onClick}
        className={className}
        target={openedNewTab ? '_blank' : '_self'}
      >
        {children}
      </RouterLink>
    );
  }

  // news or feedback or external urls
  const fullUrl = to.match(/^(http|https)/)
    ? to
    : isSWSLink
      ? `${SWS_HOST}${to}`
      : `https://${to}`;

  if (RUNTIME_ENV === 'native') {
    return (
      <a
        className={className}
        href={fullUrl}
        onClick={(e) => handleNativeExternalLinkClick(e, fullUrl)}
      >
        {children}
      </a>
    );
  }

  return (
    <a
      className={className}
      href={fullUrl}
      target="_blank"
      onClick={onClick}
      rel={isSWSLink ? '' : 'nofollow noreferrer'}
    >
      {children}
    </a>
  );
};

export default UniversalLink;
